import { Check, Minus } from 'lucide-react';
import Button from '../ui/Button';

interface PricingCardProps {
  name: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  limitations?: string[];
  badge?: string;
  highlighted?: boolean;
  ctaLabel: string;
  ctaVariant?: 'primary' | 'secondary';
  onCheckout?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export default function PricingCard({
  name,
  price,
  period,
  description,
  features,
  limitations,
  badge,
  highlighted = false,
  ctaLabel,
  ctaVariant = 'primary',
  onCheckout,
  disabled = false,
  loading = false,
}: PricingCardProps) {
  return (
    <div
      className={[
        'relative flex flex-col rounded-2xl p-8 transition-all duration-500 ease-out',
        'hover:-translate-y-1',
        highlighted
          ? 'bg-gradient-to-b from-bg-surface to-bg-elevated border-2 border-brand/40 shadow-glow-brand scale-[1.02] md:scale-[1.05] z-10'
          : 'bg-bg-surface border border-border-subtle hover:border-border-strong hover:shadow-lg',
      ]
        .join(' ')
        .trim()}
    >
      {/* Badge */}
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
          <span
            className={[
              'inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-xs font-semibold',
              'shadow-lg',
              badge === 'Limited'
                ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/30'
                : 'bg-gradient-to-r from-brand/20 to-purple-500/20 text-brand border border-brand/30',
            ]
              .join(' ')
              .trim()}
          >
            <span
              className={[
                'w-1.5 h-1.5 rounded-full',
                badge === 'Limited' ? 'bg-amber-400' : 'bg-brand',
              ].join(' ')}
            />
            {badge}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="mb-5">
        <h3 className="text-lg font-semibold text-text-primary mb-1.5">{name}</h3>
        <p className="text-sm text-text-secondary leading-relaxed">{description}</p>
      </div>

      {/* Price */}
      <div className="mb-7">
        <span className="text-4xl font-bold text-text-primary tracking-tight">{price}</span>
        {period && (
          <span className="text-sm text-text-muted ml-1.5 font-medium">{period}</span>
        )}
        {name === 'Pro' && (
          <p className="text-xs text-text-muted mt-1.5">$119/year — save 33%</p>
        )}
      </div>

      {/* CTA */}
      <Button
        variant={ctaVariant}
        size="lg"
        className="w-full mb-7"
        onClick={onCheckout}
        disabled={disabled}
        loading={loading}
      >
        {ctaLabel}
      </Button>

      {/* Features */}
      <ul className="space-y-3 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">
          What's included
        </p>
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-sm">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand/15 flex items-center justify-center mt-0.5">
              <Check size={11} className="text-brand" strokeWidth={3} />
            </span>
            <span className="text-text-secondary leading-snug">{feature}</span>
          </li>
        ))}
        {limitations?.map((limitation) => (
          <li key={limitation} className="flex items-start gap-3 text-sm">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-bg-overlay flex items-center justify-center mt-0.5">
              <Minus size={11} className="text-text-disabled" strokeWidth={3} />
            </span>
            <span className="text-text-disabled leading-snug">{limitation}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
